import { APP_INVENTORY_API_BASE } from "../../../../config/api";
/**
 * RMAList — RMA Cases page
 *
 * Route: /inventory/rma
 *
 * Shows all RMA cases for the org.
 * OEM_ADMIN sees all cases system-wide.
 * Customer org sees only their own cases.
 *
 * Features:
 *   - 4 stat cards: Open, In Progress, Resolved, Closed
 *   - DataGrid: filter by status, fault_type
 *   - Status advance buttons per row
 *   - Row click → navigate to asset detail
 *
 * APIs:
 *   GET   /api/v1/inventory/rma
 *   PATCH /api/v1/inventory/rma/:id/status
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  Chip,
  Alert,
  Skeleton,
  Stack,
  TextField,
  MenuItem,
  useTheme,
  Collapse,
  Button,
  IconButton,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import FilterListIcon from "@mui/icons-material/FilterList";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAuth } from "../../../../auth/useAuth";
import { Close, Search } from "@mui/icons-material";

const API_BASE = `${APP_INVENTORY_API_BASE}`;

const STATUS_STYLES = {
  OPEN: {
    color: "#92400e",
    bg: "#fff7ed",
    next: "IN_PROGRESS",
    nextLabel: "Mark in progress",
  },
  IN_PROGRESS: {
    color: "#1d4ed8",
    bg: "#eff6ff",
    next: "RESOLVED",
    nextLabel: "Mark resolved",
  },
  RESOLVED: {
    color: "#065f46",
    bg: "#ecfdf5",
    next: "CLOSED",
    nextLabel: "Close case",
  },
  CLOSED: { color: "#374151", bg: "#f3f4f6", next: null, nextLabel: null },
  CANCELLED: { color: "#dc2626", bg: "#fff5f5", next: null, nextLabel: null },
};

const FAULT_TYPE_LABELS = {
  HARDWARE: "Hardware",
  SOFTWARE: "Software",
  PHYSICAL_DAMAGE: "Physical damage",
  CONNECTIVITY: "Connectivity",
  OTHER: "Other",
};

const RESOLUTION_TYPES = [
  "REPAIRED",
  "REPLACED",
  "REFUNDED",
  "SCRAPPED",
  "NO_FAULT_FOUND",
];

// ─── Resolve Dialog ───────────────────────────────────────────────────────────

function ResolveDialog({
  open,
  rmaCase,
  token,
  activeOrg,
  onClose,
  onResolved,
}) {
  const [resolutionType, setResolutionType] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setResolutionType("");
      setResolutionNotes("");
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  const handleResolve = async () => {
    if (!resolutionType) {
      setError("Resolution type is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.patch(
        `${API_BASE}/rma/${rmaCase.id}/status`,
        {
          case_status: "RESOLVED",
          resolution_type: resolutionType,
          resolution_notes: resolutionNotes.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
          },
        },
      );
      onResolved(res.data.case);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to resolve case");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle fontWeight={700}>Resolve RMA case</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} mt={0.5}>
          <TextField
            select
            label="Resolution type"
            size="small"
            fullWidth
            required
            value={resolutionType}
            onChange={(e) => setResolutionType(e.target.value)}
          >
            {RESOLUTION_TYPES.map((r) => (
              <MenuItem key={r} value={r}>
                {r.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Resolution notes"
            size="small"
            fullWidth
            multiline
            rows={3}
            placeholder="What was done to resolve the issue…"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          color="success"
          onClick={handleResolve}
          disabled={submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <ArrowForwardIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
        >
          {submitting ? "Resolving…" : "Confirm resolution"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Custom toolbar ───────────────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <GridToolbarContainer
      sx={{ p: 1.5, borderBottom: "1px solid", borderColor: "divider" }}
    >
      <Box display="flex" gap={1}>
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport csvOptions={{ fileName: "rma_cases" }} />
      </Box>
    </GridToolbarContainer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────

export default function RMAList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { permissions, activeOrg } = useAuth();
  const token = localStorage.getItem("authToken");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cases, setCases] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [faultFilter, setFaultFilter] = useState("");
  const [successAlert, setSuccessAlert] = useState("");
  const [resolveTarget, setResolveTarget] = useState(null);
  const [stats, setStats] = useState({
    open: 0,
    in_progress: 0,
    resolved: 0,
    closed: 0,
    loading: true,
  });

  const [searchQuery, setSearchQuery] = useState("");

  const filteredCases = cases.filter((row) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return Object.values(row).some((val) =>
      String(val ?? "")
        .toLowerCase()
        .includes(q),
    );
  });

  const hasPermission = (p) => {
    if (!permissions || permissions.size === 0) return false;
    if (permissions.has("*")) return true;
    return permissions.has(p);
  };

  const isElevated =
    permissions?.has("*") || permissions?.has("inventory.rma.update");

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const showSuccess = (msg) => {
    setSuccessAlert(msg);
    setTimeout(() => setSuccessAlert(""), 5000);
  };

  // ── Fetch cases ──
  const fetchCases = useCallback(async () => {
    if (!hasPermission("inventory.rma.view")) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/rma`, {
        headers: authHeaders,
        params: {
          page: paginationModel.page + 1,
          per_page: paginationModel.pageSize,
          ...(statusFilter && { case_status: statusFilter }),
          ...(faultFilter && { fault_type: faultFilter }),
        },
      });
      setCases(res.data.data || []);
      setRowCount(res.data.meta?.pagination?.total_items || 0);
      setError(null);
    } catch {
      setError("Failed to load RMA cases.");
    } finally {
      setLoading(false);
    }
  }, [token, activeOrg, paginationModel, statusFilter, faultFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // ── Fetch stats ──
  const fetchStats = useCallback(() => {
    if (!hasPermission("inventory.rma.view")) return;
    Promise.all(
      ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"].map((s) =>
        axios.get(`${API_BASE}/rma`, {
          headers: authHeaders,
          params: { per_page: 1, case_status: s },
        }),
      ),
    )
      .then(([o, ip, r, c]) =>
        setStats({
          open: o.data.meta?.pagination?.total_items ?? 0,
          in_progress: ip.data.meta?.pagination?.total_items ?? 0,
          resolved: r.data.meta?.pagination?.total_items ?? 0,
          closed: c.data.meta?.pagination?.total_items ?? 0,
          loading: false,
        }),
      )
      .catch(() => setStats((prev) => ({ ...prev, loading: false })));
  }, [token, activeOrg]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ── Quick status advance (non-RESOLVED transitions) ──
  const handleQuickAdvance = async (rmaCase, newStatus) => {
    try {
      const res = await axios.patch(
        `${API_BASE}/rma/${rmaCase.id}/status`,
        { case_status: newStatus },
        { headers: authHeaders },
      );
      setCases((prev) =>
        prev.map((c) => (c.id === rmaCase.id ? res.data.case : c)),
      );
      fetchStats();
      showSuccess(`Case moved to ${newStatus.replace(/_/g, " ")}.`);
    } catch (err) {
      setError(err.response?.data?.error || "Status update failed");
    }
  };

  const handleResolved = (updatedCase) => {
    setResolveTarget(null);
    setCases((prev) =>
      prev.map((c) => (c.id === updatedCase.id ? updatedCase : c)),
    );
    fetchStats();
    showSuccess("Case resolved successfully.");
  };

  // ── Columns ──
  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
    },
    {
      field: "case_status",
      headerName: "Status",
      width: 120,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const s = STATUS_STYLES[params.value] || {
          color: "#374151",
          bg: "#f3f4f6",
        };
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Chip
              label={params.value?.replace(/_/g, " ")}
              size="small"
              sx={{
                bgcolor: s.bg,
                color: s.color,
                fontWeight: 600,
                borderRadius: "6px",
                fontSize: 11,
                height: 24,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "asset_serial_number",
      headerName: "Asset",
      flex: 1.1,
      minWidth: 140,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            color="primary.main"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {params.value || params.row.inventory_asset_id?.slice(0, 8) + "…"}
          </Typography>
          {params.row.asset_type && (
            <Typography
              variant="caption"
              noWrap
              color="text.secondary"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.row.asset_type}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "fault_type",
      headerName: "Fault",
      width: 140,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {FAULT_TYPE_LABELS[params.value] || params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "fault_description",
      headerName: "Description",
      flex: 1.5,
      minWidth: 180,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            title={params.value}
            sx={{
              fontSize: 12,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {params.value?.length > 60
              ? params.value.slice(0, 60) + "…"
              : params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "resolution_type",
      headerName: "Resolution",
      width: 140,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            sx={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {params.value?.replace(/_/g, " ") || "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "opened_at",
      headerName: "Opened",
      width: 110,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            width: "100%",
          }}
        >
          <Typography
            variant="body2"
            noWrap
            color="text.secondary"
            sx={{ fontSize: 12, whiteSpace: "nowrap" }}
          >
            {params.value
              ? new Date(params.value).toLocaleDateString("en-IN", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "_actions",
      headerName: "Action",
      width: 130,
      sortable: false,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const s = STATUS_STYLES[params.row.case_status];
        const isOpen = ["OPEN", "IN_PROGRESS"].includes(params.row.case_status);
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
          >
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="View asset">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/inventory/assets/${params.row.inventory_asset_id}`,
                    );
                  }}
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {/* Next status button */}
              {s?.next && isElevated && s.next !== "RESOLVED" && (
                <Tooltip title={s.nextLabel}>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAdvance(params.row, s.next);
                    }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Resolve button — opens dialog for resolution_type */}
              {params.row.case_status === "IN_PROGRESS" && isElevated && (
                <Tooltip title="Mark resolved">
                  <IconButton
                    size="small"
                    color="success"
                    onClick={(e) => {
                      e.stopPropagation();
                      setResolveTarget(params.row);
                    }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {/* Cancel button */}
              {isOpen && (
                <Tooltip title="Cancel case">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleQuickAdvance(params.row, "CANCELLED");
                    }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Box>
        );
      },
    },
  ];

  if (!hasPermission("inventory.rma.view")) {
    return (
      <Box p={4}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          You do not have permission to view RMA cases.
        </Alert>
      </Box>
    );
  }

  const statCards = [
    {
      title: "Open",
      value: stats.open,
      icon: <ReportProblemIcon sx={{ color: "#fcd34d" }} />,
      bg: "rgba(251, 191, 36, 0.12)",
      border: "rgba(251, 191, 36, 0.25)",
      status: "OPEN",
    },
    {
      title: "In progress",
      value: stats.in_progress,
      icon: <ReportProblemIcon sx={{ color: "#7dd3fc" }} />,
      bg: "rgba(56, 189, 248, 0.12)",
      border: "rgba(56, 189, 248, 0.25)",
      status: "IN_PROGRESS",
    },
    {
      title: "Resolved",
      value: stats.resolved,
      icon: <ReportProblemIcon sx={{ color: "#5eead4" }} />,
      bg: "rgba(20, 184, 166, 0.12)",
      border: "rgba(20, 184, 166, 0.25)",
      status: "RESOLVED",
    },
    {
      title: "Closed",
      value: stats.closed,
      icon: <ReportProblemIcon sx={{ color: "#94a3b8" }} />,
      bg: "rgba(148, 163, 184, 0.12)",
      border: "rgba(148, 163, 184, 0.25)",
      status: "CLOSED",
    },
  ];

  return (
    <Box
      sx={{
        margin: "0 auto",
        p: {
          xs: 2,
          md: 3,
          background:
            "radial-gradient(circle at top left, rgba(14, 165, 233, 0.14), transparent 30%), radial-gradient(circle at top right, rgba(16, 185, 129, 0.12), transparent 28%), linear-gradient(180deg, #F8FBFF 0%, #EEF4F8 100%)",
        },
      }}
    >
      <Box
        sx={{
          border: "1px solid rgba(148, 163, 184, 0.18)",
          background:
            "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          borderRadius: 3,
          p: 3,
          mb: 4,
        }}
      >
        {/* ── Page header ── */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
          mb={3}
        >
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ color: "#e2f8f5" ,fontSize:{xs:28,md:40} }}>
              RMA Cases
            </Typography>
            <Typography
              variant="body2"
              mt={0.3}
              sx={{ color: "rgba(203, 213, 225, 0.80)" }}
            >
              Return merchandise authorization — fault tracking and resolution.
            </Typography>
          </Box>
        </Stack>

        {/* ── Divider ── */}
        <Box
          sx={{ borderBottom: "1px solid rgba(148, 163, 184, 0.15)", mb: 3 }}
        />

        {/* ── Alerts ── */}
        <Collapse in={!!successAlert}>
          <Alert
            severity="success"
            onClose={() => setSuccessAlert("")}
            sx={{
              mb: 3,
              borderRadius: 2,
              background: "rgba(20, 184, 166, 0.12)",
              border: "1px solid rgba(20, 184, 166, 0.28)",
              color: "#5eead4",
              "& .MuiAlert-icon": { color: "#5eead4" },
              "& .MuiIconButton-root": { color: "#5eead4" },
            }}
          >
            {successAlert}
          </Alert>
        </Collapse>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              mb: 3,
              borderRadius: 2,
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.28)",
              color: "#fca5a5",
              "& .MuiAlert-icon": { color: "#fca5a5" },
              "& .MuiIconButton-root": { color: "#fca5a5" },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Stat cards — clickable to filter */}
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))"
          gap={2}
        >
          {stats.loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <Card
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                    background: "rgba(255, 255, 255, 0.04)",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={52}
                    height={52}
                    sx={{
                      mr: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(148, 163, 184, 0.10)",
                    }}
                  />
                  <Box flex={1}>
                    <Skeleton
                      variant="text"
                      width="60%"
                      sx={{ bgcolor: "rgba(148, 163, 184, 0.10)" }}
                    />
                    <Skeleton
                      variant="text"
                      width="40%"
                      height={32}
                      sx={{ bgcolor: "rgba(148, 163, 184, 0.10)" }}
                    />
                  </Box>
                </Card>
              ))
            : statCards.map((s, i) => (
                <Card
                  key={i}
                  elevation={0}
                  onClick={() => {
                    setStatusFilter(s.status);
                    setPaginationModel((p) => ({ ...p, page: 0 }));
                  }}
                  sx={{
                    borderRadius: 2.5,
                    border: "1px solid rgba(148, 163, 184, 0.15)",
                    background: "rgba(255, 255, 255, 0.04)",
                    p: 2,
                    cursor: "pointer",
                    transition: "background 0.2s, border-color 0.2s",
                    "&:hover": {
                      background: "rgba(255, 255, 255, 0.08)",
                      borderColor: "rgba(148, 163, 184, 0.30)",
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: 2,
                        bgcolor: s.bg,
                        border: `1px solid ${s.border}`,
                        mr: 2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: "rgba(203, 213, 225, 0.75)" }}
                      >
                        {s.title}
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: "#e2f8f5" }}
                      >
                        {s.value.toLocaleString()}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              ))}
        </Box>
      </Box>

      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {/* Filter bar */}
        <Box
          p={2}
          borderBottom="1px solid rgba(148, 163, 184, 0.18)"
          display="flex"
          gap={2}
          flexWrap="wrap"
          alignItems="center"
          sx={{
            background:
              "linear-gradient(135deg, rgba(15, 23, 42, 0.96), rgba(15, 118, 110, 0.88) 55%, rgba(8, 47, 73, 0.92))",
          }}
        >
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All statuses</MenuItem>
            {Object.keys(STATUS_STYLES).map((s) => (
              <MenuItem key={s} value={s}>
                {s.replace(/_/g, " ")}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Fault type"
            value={faultFilter}
            onChange={(e) => {
              setFaultFilter(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", md: 160 },
              borderRadius: 1,
              "& .MuiInputLabel-root": { color: "rgba(203, 213, 225, 0.75)" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#5eead4" },
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiSelect-icon": { color: "rgba(203, 213, 225, 0.70)" },
            }}
          >
            <MenuItem value="">All fault types</MenuItem>
            {Object.entries(FAULT_TYPE_LABELS).map(([v, l]) => (
              <MenuItem key={v} value={v}>
                {l}
              </MenuItem>
            ))}
          </TextField>

          {(statusFilter || faultFilter) && (
            <Button
              size="small"
              onClick={() => {
                setStatusFilter("");
                setFaultFilter("");
              }}
              sx={{
                textTransform: "none",
                fontSize: 12,
                height: 38,
                color: "#fca5a5",
                border: "1px solid rgba(239, 68, 68, 0.35)",
                background: "rgba(239, 68, 68, 0.10)",
                borderRadius: 1,
                "&:hover": {
                  borderColor: "rgba(239, 68, 68, 0.55)",
                  background: "rgba(239, 68, 68, 0.18)",
                },
              }}
            >
              Clear filters
            </Button>
          )}

          {/* Search bar — right side */}
          <TextField
            size="small"
            placeholder="Search cases..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPaginationModel((p) => ({ ...p, page: 0 }));
            }}
            sx={{
              width: { xs: "100%", sm: 260 },
              ml: { xs: 0, sm: "auto" },
              borderRadius: 1,
              "& .MuiOutlinedInput-root": {
                color: "#e2f8f5",
                "& fieldset": { borderColor: "rgba(148, 163, 184, 0.25)" },
                "&:hover fieldset": {
                  borderColor: "rgba(148, 163, 184, 0.45)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "rgba(20, 184, 166, 0.6)",
                },
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(148, 163, 184, 0.55)",
                opacity: 1,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    sx={{ color: "rgba(203, 213, 225, 0.60)" }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                      setPaginationModel((p) => ({ ...p, page: 0 }));
                    }}
                    sx={{
                      color: "rgba(203, 213, 225, 0.60)",
                      p: 0.3,
                      "&:hover": {
                        color: "#e2f8f5",
                        background: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {/* 
  <Box sx={{ ml: "auto" }}>
    <Typography variant="caption" color="text.secondary">
      {loading
        ? "Loading…"
        : `${rowCount.toLocaleString()} case${rowCount !== 1 ? "s" : ""}`}
    </Typography>
  </Box> */}
        </Box>

        <Box
          sx={{
            height: cases.length === 0 && !loading ? 320 : 580,
            width: "100%",
          }}
        >
          <DataGrid
            rows={filteredCases}
            rowCount={rowCount}
            loading={loading}
            paginationMode="server"
            pageSizeOptions={[15, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            columns={columns}
            getRowId={(row) => row.id}
            onRowClick={(params) => {
              if (params.field === "_actions") return;
              navigate(`/inventory/assets/${params.row.inventory_asset_id}`);
            }}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  py={6}
                >
                  <ReportProblemIcon
                    sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                  />
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.secondary"
                    gutterBottom
                  >
                    {statusFilter || faultFilter
                      ? "No cases match your filters"
                      : "No RMA cases yet"}
                  </Typography>
                  <Typography variant="body2" color="text.disabled">
                    Open a case from the asset detail page when a device is
                    faulty
                  </Typography>
                </Box>
              ),
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 600,
                color: "text.secondary",
                fontSize: 12,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
                cursor: "pointer",
              },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
            }}
          />
        </Box>
      </Card>

      {resolveTarget && (
        <ResolveDialog
          open={!!resolveTarget}
          rmaCase={resolveTarget}
          token={token}
          activeOrg={activeOrg}
          onClose={() => setResolveTarget(null)}
          onResolved={handleResolved}
        />
      )}
    </Box>
  );
}
